// Modern (D-style) Clients tab: filter chip rows, avatar rows, day-grouped
// appointments. ClientDetail overlay reuses the legacy profile for now.

import { useEffect, useMemo, useState } from "react";
import { api } from "../api.js";
import { REFERRAL_LABEL_MAP } from "../referralSources.js";
import { ClientDetail } from "./Clients.js";
import { useScrollMemory } from "../scrollMemory.js";

type SubTab = "all" | "appts";

interface ClientRow {
  id: string;
  firstName: string;
  lastName: string | null;
  phone: string | null;
  instagram: string | null;
  segment: string | null;
  referralSource: string | null;
  doNotRemind: boolean;
  blacklisted: boolean;
  visitsCount?: number;
  totalSpent?: number;
}

const SEGMENTS: { key: string; label: string; chipClass: string }[] = [
  { key: "all", label: "Всі сегменти", chipClass: "" },
  { key: "new", label: "🆕 Новачки", chipClass: "new" },
  { key: "returning", label: "🔁 Повторні", chipClass: "repeat" },
  { key: "vip", label: "💎 VIP", chipClass: "vip" },
  { key: "sleeping", label: "😴 Сплячі", chipClass: "sleep" },
  { key: "__ignored", label: "🔕 Ігноровані", chipClass: "ignore" },
];

const SOURCES: { key: string; label: string }[] = [
  { key: "all", label: "Всі джерела" },
  { key: "instagram", label: "📷 Instagram" },
  { key: "google_maps", label: "🗺️ Google Maps" },
  { key: "tiktok", label: "🎵 TikTok" },
  { key: "public", label: "📢 Паблік" },
  { key: "targeted_ads", label: "🎯 Таргет" },
  { key: "recommendation", label: "🤝 Порекомендували" },
  { key: "business_card", label: "💳 Візитка" },
  { key: "cross_marketing", label: "🔁 Крос-маркетинг" },
  { key: "__unknown", label: "Невідомо" },
];

function initials(name: string): string {
  const p = name.trim().split(/\s+/);
  return ((p[0] || "")[0] + ((p[1] || "")[0] || "")).toUpperCase();
}
function avatarGradient(name: string): string {
  let h = 0;
  for (const c of name) h = (h * 31 + c.charCodeAt(0)) >>> 0;
  const hue = h % 360;
  return `linear-gradient(150deg, hsl(${hue} 52% 70%), hsl(${(hue + 26) % 360} 50% 58%))`;
}
function segmentChip(seg: string | null): { class: string; label: string } {
  const m = SEGMENTS.find((s) => s.key === seg);
  if (m) return { class: m.chipClass, label: m.label };
  return { class: "", label: "📒 Просто в базі" };
}

export function ClientsTabModern() {
  const [sub, setSub] = useState<SubTab>("all");
  const [q, setQ] = useState("");
  const [seg, setSeg] = useState("all");
  const [src, setSrc] = useState("all");
  const [items, setItems] = useState<ClientRow[] | null>(null);
  const [total, setTotal] = useState(0);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [pageSize, setPageSize] = useState(50);
  const captureScroll = useScrollMemory(!!selectedId);
  const openClient = (id: string) => {
    captureScroll();
    setSelectedId(id);
  };

  useEffect(() => {
    const opts: any = { pageSize, page: 1 };
    if (q) opts.q = q;
    if (seg !== "all") opts.segment = seg;
    if (src !== "all") opts.referralSource = src;
    api
      .clients(opts)
      .then((r: any) => {
        setItems(r.items as ClientRow[]);
        setTotal(r.total ?? 0);
      })
      .catch(() => setItems([]));
  }, [q, seg, src, pageSize]);

  // Overlay-pattern: список ховається через display:none коли відкрита
  // деталь, але лишається у DOM зі своїм скрол-положенням, пошуком,
  // фільтрами і пагінацією. Раніше `if (selectedId) return <Detail/>`
  // анмаунтив все → скрол скидався на верх.
  return (
    <>
      <div style={{ display: selectedId ? "none" : "block" }}>
        <div className="nz-segwrap" style={{ marginTop: 6 }}>
          <button
            className="nz-seg"
            data-on={sub === "all"}
            onClick={() => setSub("all")}
          >
            Всі клієнти
          </button>
          <button
            className="nz-seg"
            data-on={sub === "appts"}
            onClick={() => setSub("appts")}
          >
            Записи
          </button>
        </div>

        {sub === "all" && (
          <>
            <div className="nz-search" style={{ marginTop: 12 }}>
              <SearchIcon />
              <input
                placeholder="Пошук — ім'я, телефон, інстаграм"
                value={q}
                onChange={(e) => setQ(e.target.value)}
              />
            </div>
            <div className="nz-filter-row">
              <SelectSheet
                title="🏷 Сегмент"
                value={seg}
                onChange={(v) => setSeg(v)}
                options={SEGMENTS.map((s) => ({ value: s.key, label: s.label }))}
              />
              <SelectSheet
                title="📍 Звідки"
                value={src}
                onChange={(v) => setSrc(v)}
                options={SOURCES.map((s) => ({ value: s.key, label: s.label }))}
              />
            </div>
            <div className="nz-countline">
              <span>
                {items?.length ?? 0} з {total} клієнтів
              </span>
              <div style={{ width: 110 }}>
                <SelectSheet<"20" | "50" | "100">
                  title="Скільки показувати?"
                  value={String(pageSize) as "20" | "50" | "100"}
                  onChange={(v) => setPageSize(Number(v))}
                  options={[
                    { value: "20", label: "20 на стор." },
                    { value: "50", label: "50 на стор." },
                    { value: "100", label: "100 на стор." },
                  ]}
                />
              </div>
            </div>
            {(items ?? []).map((c) => (
              <ClientRowBlock
                key={c.id}
                c={c}
                onOpen={() => openClient(c.id)}
              />
            ))}
            {items && items.length === 0 && (
              <div
                className="nz-section-hint"
                style={{ textAlign: "center", padding: 30 }}
              >
                Нікого не знайдено
              </div>
            )}
          </>
        )}

        {sub === "appts" && <AppointmentsView onOpen={openClient} />}
      </div>
      {selectedId && (
        <ClientDetail id={selectedId} onBack={() => setSelectedId(null)} />
      )}
    </>
  );
}

function ClientRowBlock({
  c,
  onOpen,
}: {
  c: ClientRow;
  onOpen: () => void;
}) {
  const fullName = `${c.firstName} ${c.lastName ?? ""}`.trim();
  const chip = segmentChip(c.segment);
  return (
    <button className="nz-row" onClick={onOpen}>
      <div className="nz-row-av" style={{ background: avatarGradient(fullName) }}>
        {initials(fullName)}
      </div>
      <div className="nz-row-main">
        <div className="nz-row-name">{fullName}</div>
        <div className="nz-row-sub">
          <span className={"nz-chip " + chip.class}>{chip.label}</span>
          {c.instagram ? "@" + c.instagram : c.phone}
        </div>
      </div>
      <div className="nz-row-right">
        {(c.visitsCount ?? 0) > 0 ? (
          <>
            <span className="v">{(c.totalSpent ?? 0).toLocaleString("uk")} ₴</span>
            <span className="s">{c.visitsCount} віз.</span>
          </>
        ) : (
          <ChevronRight />
        )}
      </div>
    </button>
  );
}

// Palette для лівого бордера appointment-карти.
// Тільки для майстрів зі ⭐ — щоб серед "своїх" були візуально різні.
// 8 контрастних відтінків — рожевий, фіалковий, бірюзовий, бурштин, небо,
// червоний, фіолетовий, зелений.
const STAR_COLORS = [
  "#E85C97",
  "#6E5BD6",
  "#22C5A8",
  "#F2A23A",
  "#5DAEEC",
  "#E85C5C",
  "#A66FE8",
  "#3FB76C",
];

function hashStr(s: string): number {
  let h = 0;
  for (const c of s) h = (h * 31 + c.charCodeAt(0)) >>> 0;
  return h;
}

function AppointmentsView({ onOpen }: { onOpen: (id: string) => void }) {
  const [items, setItems] = useState<any[] | null>(null);
  const [masters, setMasters] = useState<
    { id: string; name: string; starred: boolean }[]
  >([]);
  const [mode, setMode] = useState<"cal" | "list">("cal");
  const [selectedDay, setSelectedDay] = useState<string>(() => new Date().toDateString());
  // 0 = поточний тиждень (понеділок–неділя від сьогодні).
  // ±N — наступні / попередні тижні, без верхньої межі: Bumpix
  // може мати записи за пів-року вперед, нехай прокручується.
  const [weekOffset, setWeekOffset] = useState(0);
  // "" = всі майстри. Фільтр зберігається лише в памʼяті сесії —
  // власниця часто хоче глянути «лише по Дарині» і потім вернутись
  // до загального виду; зберігати в localStorage було б нав'язливо.
  const [masterId, setMasterId] = useState<string>("");
  useEffect(() => {
    api.scheduledVisits().then((d) => setItems(d as any[])).catch(() => setItems([]));
    api
      .masters()
      .then((d) =>
        setMasters(
          (d as any[]).map((m) => ({
            id: m.id,
            name: m.name ?? "—",
            starred: !!m.starred,
          })),
        ),
      )
      .catch(() => setMasters([]));
  }, []);

  // Майстер-фільтр застосовується ДО всіх агрегацій (weekDays, dayAppts,
  // byDay), щоб у тижневій смужці точки появлялись тільки на тих днях де
  // у вибраного майстра реально є запис.
  const filteredItems = useMemo(() => {
    if (!items) return null;
    if (!masterId) return items;
    return items.filter((v) => v.master?.id === masterId);
  }, [items, masterId]);

  // Опції dropdown: «Всі майстри» + список з salonу. Зірочкових — нагору,
  // щоб «свої» були під рукою; інших — нижче як другу групу.
  const masterOptions = useMemo(() => {
    const starred = masters.filter((m) => m.starred);
    const rest = masters.filter((m) => !m.starred);
    return [
      { value: "", label: "Всі майстри" },
      ...starred.map((m) => ({
        value: m.id,
        label: `⭐ ${m.name}`,
        group: "Свої",
      })),
      ...rest.map((m) => ({
        value: m.id,
        label: `💁‍♀️ ${m.name}`,
        group: "Інші",
      })),
    ];
  }, [masters]);

  // Map id → color (тільки для зірочкових)
  const masterColor = useMemo(() => {
    const m = new Map<string, string>();
    for (const ms of masters) {
      if (ms.starred) {
        m.set(ms.id, STAR_COLORS[hashStr(ms.id) % STAR_COLORS.length]!);
      }
    }
    return m;
  }, [masters]);

  // week stripe for calendar — anchor = понеділок поточного тижня + offset*7
  const weekDays = useMemo(() => {
    const days: {
      label: string;
      num: number;
      dateKey: string;
      hasAppt: boolean;
      isToday: boolean;
    }[] = [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const todayKey = today.toDateString();
    const monday = new Date(today);
    monday.setDate(today.getDate() - ((today.getDay() + 6) % 7) + weekOffset * 7);
    const wd = ["Пн", "Вт", "Ср", "Чт", "Пт", "Сб", "Нд"];
    for (let i = 0; i < 7; i++) {
      const d = new Date(monday);
      d.setDate(monday.getDate() + i);
      const dk = d.toDateString();
      const hasAppt =
        (filteredItems ?? []).some(
          (v) => new Date(v.startedAt).toDateString() === dk,
        );
      days.push({
        label: wd[i]!,
        num: d.getDate(),
        dateKey: dk,
        hasAppt,
        isToday: dk === todayKey,
      });
    }
    return days;
  }, [filteredItems, weekOffset]);

  // Підпис над смужкою — діапазон тижня, типу «3–9 червня» або
  // «28 травня – 3 червня» якщо тиждень перехоплює стик місяців.
  const weekLabel = useMemo(() => {
    if (weekDays.length < 7) return "";
    const months = [
      "січня", "лютого", "березня", "квітня", "травня", "червня",
      "липня", "серпня", "вересня", "жовтня", "листопада", "грудня",
    ];
    const start = new Date(weekDays[0]!.dateKey);
    const end = new Date(weekDays[6]!.dateKey);
    if (start.getMonth() === end.getMonth()) {
      return `${start.getDate()}–${end.getDate()} ${months[start.getMonth()]}`;
    }
    return `${start.getDate()} ${months[start.getMonth()]} – ${end.getDate()} ${months[end.getMonth()]}`;
  }, [weekDays]);

  const dayAppts = useMemo(() => {
    return (filteredItems ?? [])
      .filter((v) => new Date(v.startedAt).toDateString() === selectedDay)
      .sort((a, b) => new Date(a.startedAt).getTime() - new Date(b.startedAt).getTime());
  }, [filteredItems, selectedDay]);

  const byDay = useMemo(() => {
    if (!filteredItems) return [];
    const map = new Map<string, any[]>();
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const tom = new Date(today.getTime() + 86400000);
    for (const v of filteredItems) {
      const d = new Date(v.startedAt);
      const dayKey = d.toDateString();
      const arr = map.get(dayKey) ?? [];
      arr.push(v);
      map.set(dayKey, arr);
    }
    return Array.from(map.entries()).map(([k, arr]) => {
      const d = new Date(k);
      let label = "";
      if (d.toDateString() === today.toDateString()) label = "🌇 Сьогодні";
      else if (d.toDateString() === tom.toDateString()) label = "☀️ Завтра";
      else
        label = `${["Нд", "Пн", "Вт", "Ср", "Чт", "Пт", "Сб"][d.getDay()]}, ${d.getDate()}.${String(d.getMonth() + 1).padStart(2, "0")}`;
      return { label, items: arr };
    });
  }, [filteredItems]);

  if (!items) return <div className="hint" style={{ marginTop: 14 }}>Завантаження…</div>;

  const selectedMaster = masters.find((m) => m.id === masterId);
  return (
    <>
      {masters.length > 0 && (
        <div style={{ marginTop: 14 }}>
          <SelectSheet
            title="💁‍♀️ Фільтр по майстру"
            value={masterId}
            onChange={(v) => setMasterId(v)}
            options={masterOptions}
            placeholder={
              selectedMaster
                ? `${selectedMaster.starred ? "⭐" : "💁‍♀️"} ${selectedMaster.name}`
                : "Всі майстри"
            }
          />
        </div>
      )}

      <div className="nz-segwrap" style={{ marginTop: 10 }}>
        <button className="nz-seg" data-on={mode === "cal"} onClick={() => setMode("cal")}>
          📅 Календар
        </button>
        <button className="nz-seg" data-on={mode === "list"} onClick={() => setMode("list")}>
          📋 Список
        </button>
      </div>

      {mode === "cal" ? (
        <>
          {/* Week navigator: ‹ — діапазон — › + кнопка повернення до
              сьогодні, що зʼявляється коли тиждень не поточний. */}
          <div className="nz-wknav">
            <button
              type="button"
              className="nz-wknav-btn"
              onClick={() => setWeekOffset((o) => o - 1)}
              aria-label="Попередній тиждень"
            >
              ‹
            </button>
            <div className="nz-wknav-label">
              <span>{weekLabel}</span>
              {weekOffset !== 0 && (
                <button
                  type="button"
                  className="nz-wknav-today"
                  onClick={() => {
                    setWeekOffset(0);
                    setSelectedDay(new Date().toDateString());
                  }}
                >
                  → Сьогодні
                </button>
              )}
            </div>
            <button
              type="button"
              className="nz-wknav-btn"
              onClick={() => setWeekOffset((o) => o + 1)}
              aria-label="Наступний тиждень"
            >
              ›
            </button>
          </div>

          {/* week stripe — soft pink pill containing 7 day cells */}
          <div className="nz-week" style={{ marginTop: 10 }}>
            {weekDays.map((d, i) => (
              <button
                key={i}
                className="nz-wd"
                data-on={d.dateKey === selectedDay}
                onClick={() => setSelectedDay(d.dateKey)}
              >
                <div className="wn">{d.label}</div>
                <div className="dn">{d.num}</div>
                {d.hasAppt ? <div className="dot" /> : <div style={{ height: 8 }} />}
              </button>
            ))}
          </div>

          {/* friendly day title */}
          {(() => {
            const sel = new Date(selectedDay);
            const todayKey = new Date().toDateString();
            const tomKey = new Date(Date.now() + 86400000).toDateString();
            const wd = ["Нд", "Пн", "Вт", "Ср", "Чт", "Пт", "Сб"][sel.getDay()];
            const human =
              selectedDay === todayKey
                ? "🌇 Сьогодні"
                : selectedDay === tomKey
                  ? "☀️ Завтра"
                  : `${wd}, ${sel.getDate()}.${String(sel.getMonth() + 1).padStart(2, "0")}`;
            return (
              <div className="nz-day-title">
                <span>{human}</span>
                <span className="ds">
                  · {dayAppts.length} візит{dayAppts.length === 1 ? "" : dayAppts.length < 5 ? "и" : "ів"}
                  {dayAppts.length > 0 &&
                    ` · ${uah(dayAppts.reduce((s, a) => s + Number(a.price ?? 0), 0))} ₴`}
                </span>
              </div>
            );
          })()}

          <div className="nz-tl-wrap">
            {dayAppts.length === 0 ? (
              <div className="nz-tl-empty">
                <span className="em">🌸</span>
                Цього дня записів нема
              </div>
            ) : (
              dayAppts.map((a: any) => {
                const d = new Date(a.startedAt);
                const hhmm = `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
                const name = a.client
                  ? `${a.client.firstName} ${a.client.lastName ?? ""}`.trim()
                  : "—";
                const cls =
                  (a.serviceLabel ?? a.service ?? "").toLowerCase().includes("педи") ? "pedi" : "mani";
                const accent = a.master?.id
                  ? masterColor.get(a.master.id)
                  : undefined;
                return (
                  <div className="nz-tl-row" key={a.id}>
                    <div className="nz-tl-time">{hhmm}</div>
                    <button
                      className={`nz-tl-appt ${cls}`}
                      onClick={() => a.client?.id && onOpen(a.client.id)}
                      style={{
                        textAlign: "left",
                        width: "100%",
                        cursor: "pointer",
                        fontFamily: "inherit",
                        ...(accent ? { borderLeftColor: accent } : {}),
                      }}
                    >
                      <div className="an">{name}</div>
                      <div className="as">
                        {a.serviceLabel ?? a.service ?? "—"}
                        {Number(a.price ?? 0) > 0 ? ` · ₴${uah(Number(a.price))}` : ""}
                      </div>
                      {a.master?.name && <div className="am">💁‍♀️ {a.master.name}</div>}
                    </button>
                  </div>
                );
              })
            )}
          </div>
        </>
      ) : (
        <>
          {(filteredItems ?? []).length === 0 ? (
            <div className="nz-section-hint" style={{ textAlign: "center", padding: 30 }}>
              {masterId
                ? `Записів у ${selectedMaster?.name ?? "цього майстра"} попереду нема`
                : "Записів попереду нема"}
            </div>
          ) : (
            <>
              <div className="nz-summary">
                Заплановані візити з <b>Bumpix</b> · всього{" "}
                {(filteredItems ?? []).length}
                {masterId && selectedMaster
                  ? ` · ${selectedMaster.starred ? "⭐" : "💁‍♀️"} ${selectedMaster.name}`
                  : ""}
              </div>
              {byDay.map((g) => (
                <div className="nz-daygroup" key={g.label}>
                  <div className="nz-dayhead">
                    {g.label} <span className="cnt">· {g.items.length}</span>
                  </div>
                  {g.items.map((a: any) => {
                    const d = new Date(a.startedAt);
                    const hhmm = `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
                    const day = `${String(d.getDate()).padStart(2, "0")}.${String(d.getMonth() + 1).padStart(2, "0")}`;
                    const name = a.client
                      ? `${a.client.firstName} ${a.client.lastName ?? ""}`.trim()
                      : "—";
                    const accent = a.master?.id
                      ? masterColor.get(a.master.id)
                      : undefined;
                    return (
                      <button
                        key={a.id}
                        onClick={() => a.client?.id && onOpen(a.client.id)}
                        className="nz-appt"
                        style={{
                          width: "100%",
                          textAlign: "left",
                          cursor: "pointer",
                          ...(accent ? { borderLeftColor: accent } : {}),
                        }}
                      >
                        <div className="nz-appt-time">
                          <div className="t">{hhmm}</div>
                          <div className="d">{day}</div>
                        </div>
                        <div className="nz-appt-main">
                          <div className="nz-appt-name">{name}</div>
                          <div className="nz-appt-sub">
                            {a.client?.phone}
                            {a.client?.instagram ? ` · @${a.client.instagram}` : ""}
                          </div>
                        </div>
                        <div className="nz-appt-right">
                          <div className="tag">{a.serviceLabel ?? a.service}</div>
                          {a.master?.name && <div className="m">💁‍♀️ {a.master.name}</div>}
                        </div>
                      </button>
                    );
                  })}
                </div>
              ))}
            </>
          )}
        </>
      )}
    </>
  );
}

// Точна сума до 1 грн з розділювачем тисяч ("16 057", не "16k") — люди
// плутались у заокруглених «k». Групування пробілами, знак зберігається.
function uah(n: number): string {
  return String(Math.round(n)).replace(/\B(?=(\d{3})+(?!\d))/g, " ");
}

function SearchIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      style={{ color: "var(--ink-3)" }}
    >
      <circle cx="11" cy="11" r="6.5" stroke="currentColor" strokeWidth="1.9" />
      <path
        d="m20 20-3.5-3.5"
        stroke="currentColor"
        strokeWidth="1.9"
        strokeLinecap="round"
      />
    </svg>
  );
}

function ChevronRight() {
  return (
    <svg
      width="9"
      height="14"
      viewBox="0 0 9 14"
      fill="none"
      style={{ color: "var(--ink-3)" }}
    >
      <path
        d="m1.5 1 6 6-6 6"
        stroke="currentColor"
        strokeWidth="2.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

// ============================================================
// ClientDetailModern — full pink-pastel client profile
// ============================================================

import { api as apiMod } from "../api.js";
import { SelectSheet } from "../components/SelectSheet.js";
import { ConfirmSheet } from "../components/ConfirmSheet.js";

export function ClientDetailModern({ id, onBack }: { id: string; onBack: () => void }) {
  const [data, setData] = useState<any | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [visitsShown, setVisitsShown] = useState(10);
  const [editorOpen, setEditorOpen] = useState(false);
  const [isOwner, setIsOwner] = useState(false);
  const [bloggerConfirm, setBloggerConfirm] = useState(false);
  const [remindBusy, setRemindBusy] = useState(false);
  const [googleBusy, setGoogleBusy] = useState(false);

  useEffect(() => {
    setData(null);
    setVisitsShown(10);
    apiMod
      .clientDetail(id)
      .then(setData)
      .catch((e) => setError(String(e)));
  }, [id]);

  useEffect(() => {
    api.me().then((m: any) => setIsOwner(!!m?.isOwner)).catch(() => setIsOwner(false));
  }, []);

  if (error)
    return (
      <>
        <button className="nz-backbtn" onClick={onBack}>← Назад</button>
        <div className="nz-sec" style={{ color: "var(--red-ink)" }}>
          Помилка: {error}
        </div>
      </>
    );
  if (!data)
    return (
      <>
        <button className="nz-backbtn" onClick={onBack}>← Назад</button>
        <div className="hint" style={{ marginTop: 14 }}>Завантаження…</div>
      </>
    );

  const fullName = `${data.firstName} ${data.lastName ?? ""}`.trim();
  const visits = (data.visits as any[]) ?? [];
  const completed = visits.filter((v) => v.status === "completed");
  const totalSpent = completed.reduce((s, v) => s + Number(v.price ?? 0), 0);
  const totalTips = completed.reduce((s, v) => s + Number(v.tip ?? 0), 0);
  const slice = visits.slice(0, visitsShown);

  return (
    <>
      <button className="nz-backbtn" onClick={onBack}>← Назад</button>

      <div className="nz-detail-head">
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12 }}>
          <div style={{ minWidth: 0 }}>
            <div className="nz-detail-name">{fullName}</div>
            <div className="nz-detail-sub">
              {data.phone ?? "—"}
              {data.segment ? ` · ${segmentChip(data.segment).label}` : ""}
            </div>
          </div>
          <button
            className="nz-btn primary"
            style={{ height: 38, padding: "0 14px" }}
            onClick={() => setEditorOpen(true)}
          >
            ✏️ Редагувати
          </button>
        </div>
        <div className="nzd-contacts" style={{ marginTop: 13 }}>
          {data.phone && (
            <>
              <button
                className="nzd-cbtn viber"
                onClick={() => window.location.assign(`viber://chat?number=${encodeURIComponent(data.phone)}`)}
              >
                <span style={{ fontSize: 18 }}>💬</span>
              </button>
              <button
                className="nzd-cbtn call"
                onClick={() => window.location.assign(`tel:${data.phone}`)}
              >
                <span style={{ fontSize: 18 }}>📞</span>
              </button>
            </>
          )}
          {data.instagram && (
            <button
              className="nzd-cbtn insta"
              onClick={() => window.open(`https://instagram.com/${data.instagram}`, "_blank")}
            >
              <span style={{ fontSize: 18 }}>📷</span>
            </button>
          )}
          {data.telegramUsername && (
            <button
              className="nzd-cbtn tg"
              onClick={() => window.open(`https://t.me/${data.telegramUsername}`, "_blank")}
            >
              <span style={{ fontSize: 18 }}>✈️</span>
            </button>
          )}
        </div>
        <div className="nz-kpis k3" style={{ marginTop: 13 }}>
          <div className="nz-kpi sm">
            <div className="l">Візитів</div>
            <div className="v">{completed.length}</div>
          </div>
          <div className="nz-kpi sm">
            <div className="l">Витрачено</div>
            <div className="v">{totalSpent.toLocaleString("uk")} ₴</div>
          </div>
          <div className="nz-kpi sm">
            <div className="l">Чайових</div>
            <div className="v">{totalTips.toLocaleString("uk")} ₴</div>
          </div>
        </div>
        <div className="nz-qa">
          <button
            type="button"
            className="q"
            disabled={remindBusy}
            onClick={async () => {
              if (remindBusy) return;
              const next = !data.doNotRemind; // doNotRemind=true → нагадування ВИМК
              setRemindBusy(true);
              try {
                const r = await api.setClientDoNotRemind(data.id, next);
                setData({ ...data, doNotRemind: r.doNotRemind });
              } catch (e) {
                alert(String(e));
              } finally {
                setRemindBusy(false);
              }
            }}
            style={{
              border: 0,
              cursor: remindBusy ? "wait" : "pointer",
              fontFamily: "inherit",
              opacity: remindBusy ? 0.6 : 1,
              background: data.doNotRemind
                ? "rgba(127,127,127,0.12)"
                : "var(--rose-50)",
              color: data.doNotRemind ? "var(--ink-2)" : "var(--rose-700)",
            }}
            title={
              data.doNotRemind
                ? "Натисни щоб УВІМКНУТИ нагадування про цю клієнтку"
                : "Натисни щоб ВИМКНУТИ нагадування про цю клієнтку"
            }
          >
            {data.doNotRemind ? "🔕 Не нагадуємо" : "🔔 Нагадування йдуть"}
          </button>
          <button
            type="button"
            className="q"
            disabled={googleBusy}
            onClick={async () => {
              if (googleBusy) return;
              const next = !data.googleReviewLeft;
              setGoogleBusy(true);
              try {
                const r = await api.setClientGoogleReview(data.id, next);
                setData({ ...data, googleReviewLeft: r.googleReviewLeft });
              } catch (e) {
                alert(String(e));
              } finally {
                setGoogleBusy(false);
              }
            }}
            style={{
              border: 0,
              cursor: googleBusy ? "wait" : "pointer",
              fontFamily: "inherit",
              opacity: googleBusy ? 0.6 : 1,
              background: data.googleReviewLeft
                ? "rgba(245, 200, 66, 0.18)"
                : "var(--chip)",
              color: data.googleReviewLeft ? "#9a6a00" : "var(--ink-2)",
            }}
            title={
              data.googleReviewLeft
                ? "Натисни якщо НЕ лишала Google-відгук"
                : "Натисни якщо клієнтка лишила Google-відгук"
            }
          >
            {data.googleReviewLeft ? "🌟 Залишила Google-відгук" : "☆ Google — ні"}
          </button>
          {data.isBlogger && <span className="q" style={{ color: "var(--rose-700)" }}>📣 Блогер</span>}
        </div>
        <button
          type="button"
          onClick={() => setBloggerConfirm(true)}
          style={{
            marginTop: 10,
            padding: "9px 13px",
            borderRadius: 11,
            border: data.isBlogger
              ? "1px solid rgba(127,127,127,0.35)"
              : "1.5px dashed var(--rose-300)",
            background: data.isBlogger ? "var(--surface-2)" : "var(--rose-50)",
            color: data.isBlogger ? "var(--ink-2)" : "var(--rose-700)",
            fontFamily: "inherit",
            fontSize: 13,
            fontWeight: 700,
            cursor: "pointer",
          }}
        >
          {data.isBlogger ? "✖ Прибрати тег «Блогер»" : "📣 Позначити як блогера"}
        </button>
      </div>

      <div className="nz-sec">
        <div className="nz-sec-title">📍 Звідки дізналась</div>
        <ReferralSourceModern
          clientId={data.id}
          initial={data.referralSource ?? null}
          onSaved={(src) => setData({ ...data, referralSource: src })}
        />
      </div>

      <div className="nz-sec">
        <div className="nz-sec-title">💸 Знижки</div>
        <ClientDiscountsModern clientId={data.id} />
      </div>

      <div className="nz-sec">
        <div className="nz-sec-title">💬 Коментарі / хто є хто</div>
        <ClientNotesModern clientId={data.id} initial={data.appNotes ?? []} />
      </div>

      {visits.length === 0 ? (
        <div className="nz-sec-sub" style={{ textAlign: "center", padding: 30 }}>
          Ще не була у нас.
        </div>
      ) : (
        <>
          <div className="nz-sec-title" style={{ margin: "18px 4px 10px" }}>
            Історія візитів · {Math.min(visitsShown, visits.length)} з {visits.length}
          </div>
          {slice.map((v: any) => {
            const d = new Date(v.startedAt);
            const dd = `${String(d.getDate()).padStart(2, "0")}.${String(d.getMonth() + 1).padStart(2, "0")}`;
            const hhmm = `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
            const price = Number(v.price ?? 0);
            return (
              <div key={v.id} className="nz-appt">
                <div className="nz-appt-time">
                  <div className="t">{dd}</div>
                  <div className="d">{hhmm}</div>
                </div>
                <div className="nz-appt-main">
                  <div className="nz-appt-name">{v.serviceLabel ?? v.service ?? "—"}</div>
                  {v.master?.name && <div className="nz-appt-sub">💁‍♀️ {v.master.name}</div>}
                </div>
                <div className="nz-appt-right">
                  <div className="tag green-t">{price > 0 ? `${price.toLocaleString("uk")} ₴` : "—"}</div>
                </div>
              </div>
            );
          })}
          {visitsShown < visits.length && (
            <button
              className="nz-showmore"
              onClick={() => setVisitsShown(visitsShown + 10)}
            >
              ⬇ Показати ще ({visits.length - visitsShown})
            </button>
          )}
        </>
      )}

      {editorOpen && (
        <ContactsEditorSheet
          clientId={data.id}
          initial={{
            phone: data.phone ?? "",
            instagram: data.instagram ?? "",
            telegramUsername: data.telegramUsername ?? "",
            viberPhone: data.viberPhone ?? "",
          }}
          onClose={() => setEditorOpen(false)}
          onSaved={(next) => {
            setData({ ...data, ...next });
            setEditorOpen(false);
          }}
        />
      )}

      <ConfirmSheet
        open={bloggerConfirm}
        onClose={() => setBloggerConfirm(false)}
        title={
          data.isBlogger
            ? `Прибрати тег «Блогер» у «${data.firstName}»?`
            : `Позначити «${data.firstName}» як блогера?`
        }
        body={
          data.isBlogger ? (
            <>
              <p style={{ margin: "0 0 6px" }}>
                Вона перестане бути блогеркою — її візити знову рахуватимуться
                як справжні клієнтські, і їй знов почнуть приходити нагадування
                (як пройшов візит, час на rebook тощо).
              </p>
              <p style={{ margin: 0 }}>
                Роби це коли блогерка вирішила стати звичайною клієнткою.
              </p>
            </>
          ) : (
            <>
              <p style={{ margin: "0 0 6px" }}>
                Блогери — це проморобота, а не платні клієнти. Бот перестане:
              </p>
              <ul style={{ margin: "0 0 8px", paddingLeft: 18 }}>
                <li>питати «звідки дізналась» (ми її самі запросили)</li>
                <li>пушити «час на rebook» через місяць / 3 / 6 / 12</li>
                <li>рахувати її як «нову клієнтку» у статистиці</li>
              </ul>
              <p style={{ margin: "0 0 6px" }}>
                <b>Followup «як пройшла послуга»</b> продовжує приходити — це
                частина бартеру, тобі потрібен фідбек.
              </p>
              <p style={{ margin: 0 }}>
                Активні rebook-нагадування для неї одразу видаляться. Скасувати
                можна будь-коли — тут же.
              </p>
            </>
          )
        }
        confirmLabel={data.isBlogger ? "✖ Прибрати тег" : "📣 Так, це блогер"}
        tone={data.isBlogger ? "danger" : "primary"}
        onConfirm={async () => {
          try {
            const r = await api.setClientBlogger(data.id, !data.isBlogger);
            setData({ ...data, isBlogger: r.isBlogger });
            setBloggerConfirm(false);
          } catch (e) {
            alert(String(e));
          }
        }}
      />
    </>
  );
}

function ContactsEditorSheet({
  clientId,
  initial,
  onClose,
  onSaved,
}: {
  clientId: string;
  initial: {
    phone: string;
    instagram: string;
    telegramUsername: string;
    viberPhone: string;
  };
  onClose: () => void;
  onSaved: (next: {
    phone: string | null;
    instagram: string | null;
    telegramUsername: string | null;
    viberPhone: string | null;
  }) => void;
}) {
  const [phone, setPhone] = useState(initial.phone);
  const [instagram, setInstagram] = useState(initial.instagram);
  const [tg, setTg] = useState(initial.telegramUsername);
  const [viber, setViber] = useState(initial.viberPhone);
  const [busy, setBusy] = useState(false);

  async function save() {
    if (busy) return;
    setBusy(true);
    try {
      const next = await apiMod.updateClientContacts(clientId, {
        phone: phone.trim(),
        instagram: instagram.trim().replace(/^@/, ""),
        telegramUsername: tg.trim().replace(/^@/, ""),
        viberPhone: viber.trim(),
      });
      onSaved(next as any);
    } catch (e) {
      alert(String(e));
      setBusy(false);
    }
  }

  return (
    <>
      <div className="nz-dim" onClick={onClose} />
      <div className="nz-sheet">
        <div className="nz-sheet-grip" />
        <div className="nz-sheet-title">✏️ Редагування контактів</div>
        <Field label="📞 Телефон" value={phone} onChange={setPhone} placeholder="+380…" hint="Для Viber та дзвінків" />
        <Field label="✈️ Telegram username" value={tg} onChange={setTg} placeholder="username (без @)" hint="Без @ — додамо самі" />
        <Field label="📷 Instagram" value={instagram} onChange={setInstagram} placeholder="handle" hint="Для прямих повідомлень" />
        <Field label="💬 Viber-номер (опц)" value={viber} onChange={setViber} placeholder="якщо інший" hint="Залиш порожнім — підтягне з телефону" />
        <div className="nz-script-actions" style={{ marginTop: 12 }}>
          <button
            className={"nz-btn green flex" + (busy ? " disabled" : "")}
            onClick={save}
          >
            ✓ Зберегти
          </button>
          <button className="nz-btn ghost" onClick={onClose}>
            ✕ Скасувати
          </button>
        </div>
      </div>
    </>
  );
}

function Field({
  label,
  value,
  onChange,
  placeholder,
  hint,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  hint?: string;
}) {
  return (
    <div className="nz-fieldrow">
      <label>{label}</label>
      <input
        className="nz-input"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
      />
      {hint && <div className="hint">{hint}</div>}
    </div>
  );
}

function ClientNotesModern({
  clientId,
  initial,
}: {
  clientId: string;
  initial: { id: string; text: string; createdAt: string }[];
}) {
  const [notes, setNotes] = useState(initial);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);

  async function add() {
    if (!draft.trim() || saving) return;
    setSaving(true);
    try {
      const n = await apiMod.addClientNote(clientId, draft.trim());
      setNotes([n as any, ...notes]);
      setDraft("");
    } finally {
      setSaving(false);
    }
  }

  return (
    <>
      <div style={{ display: "flex", gap: 6, marginTop: 8 }}>
        <input
          className="nz-input"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="Напр. «просила не дзвонити по обіді»"
        />
        <button
          className={"nz-btn primary" + (!draft.trim() || saving ? " disabled" : "")}
          onClick={add}
          style={{ height: 42 }}
        >
          ＋
        </button>
      </div>
      {notes.length === 0 ? (
        <p className="hint" style={{ marginTop: 10, marginBottom: 0 }}>
          Поки нема коментарів. Вони з'являються коли адмін відмічає що
          відповіла клієнтка у нагадуванні.
        </p>
      ) : (
        <div style={{ marginTop: 10 }}>
          {notes.slice(0, 10).map((n) => (
            <div
              key={n.id}
              style={{
                borderLeft: "3px solid var(--rose-300)",
                paddingLeft: 10,
                marginBottom: 8,
                background: "var(--surface-2)",
                borderRadius: 8,
                padding: "8px 10px",
              }}
            >
              <div style={{ fontSize: 13, color: "var(--ink)" }}>{n.text}</div>
              <div className="hint" style={{ fontSize: 11, marginTop: 4 }}>
                {new Date(n.createdAt).toLocaleString("uk-UA", {
                  day: "2-digit",
                  month: "2-digit",
                  year: "2-digit",
                  hour: "2-digit",
                  minute: "2-digit",
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </>
  );
}

// ============================================================
// ReferralSourceModern — «звідки дізналась» (модерн)
// ============================================================
// Список джерел — спільний із карткою задачі, див. referralSources.ts.

function ReferralSourceModern({
  clientId,
  initial,
  onSaved,
}: {
  clientId: string;
  initial: string | null;
  onSaved: (src: string | null) => void;
}) {
  const isKnown = (v: string) => v in REFERRAL_LABEL_MAP;
  const initialIsCustom = !!initial && !isKnown(initial);

  const [select, setSelect] = useState<string>(
    initial ? (initialIsCustom ? "__other" : initial) : "",
  );
  const [customText, setCustomText] = useState<string>(
    initialIsCustom ? initial! : "",
  );
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<number>(0);

  async function persist(source: string) {
    if (saving) return;
    setSaving(true);
    try {
      const r = await apiMod.setClientReferralSource(clientId, source);
      onSaved(r.referralSource);
      setSavedAt(Date.now());
    } catch (e) {
      alert(String(e));
    } finally {
      setSaving(false);
    }
  }

  function onSelectChange(v: string) {
    setSelect(v);
    if (v !== "__other") void persist(v);
  }

  return (
    <div style={{ marginTop: 10 }}>
      <SelectSheet
        title="📍 Звідки дізналась"
        value={select}
        onChange={onSelectChange}
        disabled={saving}
        placeholder="— Не вказано —"
        options={[
          ...Object.entries(REFERRAL_LABEL_MAP).map(([key, label]) => ({
            value: key,
            label,
          })),
          { value: "__other", label: "✏️ Інше (вписати вручну)" },
        ]}
      />

      {select === "__other" && (
        <div style={{ display: "flex", gap: 6, marginTop: 8 }}>
          <input
            type="text"
            placeholder="Напр. подруга Софія / банер"
            value={customText}
            onChange={(e) => setCustomText(e.target.value)}
            style={{
              flex: 1,
              padding: "10px 12px",
              border: "1px solid var(--line)",
              borderRadius: 12,
              background: "var(--surface)",
              color: "var(--ink)",
              fontSize: 14,
              fontFamily: "inherit",
            }}
          />
          <button
            className="nz-btn primary"
            disabled={saving || !customText.trim()}
            onClick={() => persist(customText.trim())}
            style={{ height: 42, padding: "0 14px" }}
          >
            Зберегти
          </button>
        </div>
      )}
      {Date.now() - savedAt < 2500 && savedAt > 0 && (
        <div style={{ fontSize: 12, color: "var(--rose-700)", marginTop: 6 }}>
          ✓ збережено
        </div>
      )}
    </div>
  );
}

// ============================================================
// ClientDiscountsModern — список активних знижок + швидке додавання
// ============================================================
type ClientDiscount = {
  id: string;
  text: string;
  status: "active" | "applied" | "cancelled";
  createdAt: string;
  appliedAt: string | null;
  cancelledAt: string | null;
};

function ClientDiscountsModern({ clientId }: { clientId: string }) {
  const [items, setItems] = useState<ClientDiscount[] | null>(null);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const [showForm, setShowForm] = useState(false);

  function load() {
    apiMod
      .listClientDiscounts(clientId)
      .then((d) => setItems(d as ClientDiscount[]))
      .catch(() => setItems([]));
  }
  useEffect(load, [clientId]);

  async function create() {
    if (!draft.trim()) return;
    setBusy(true);
    try {
      await apiMod.createDiscount(clientId, draft.trim());
      setDraft("");
      setShowForm(false);
      load();
    } finally {
      setBusy(false);
    }
  }
  async function applied(id: string) {
    await apiMod.markDiscountApplied(id);
    load();
  }
  async function cancel(id: string) {
    if (!confirm("Скасувати знижку?")) return;
    await apiMod.cancelDiscount(id);
    load();
  }

  if (!items) return <div className="hint">Завантаження…</div>;
  const active = items.filter((d) => d.status === "active");
  const history = items.filter((d) => d.status !== "active");

  return (
    <div style={{ marginTop: 6 }}>
      {active.length === 0 && !showForm && (
        <div className="nz-sec-sub" style={{ marginBottom: 8 }}>
          Активних знижок нема
        </div>
      )}
      {active.map((d) => (
        <div
          key={d.id}
          style={{
            display: "flex",
            gap: 8,
            alignItems: "center",
            padding: "10px 12px",
            background: "var(--rose-50)",
            border: "1px solid var(--rose-100)",
            borderRadius: 12,
            marginBottom: 8,
          }}
        >
          <span style={{ flex: 1, minWidth: 0, fontWeight: 700, color: "var(--rose-700)" }}>
            🎁 {d.text}
          </span>
          <button
            className="nz-btn tiny"
            onClick={() => applied(d.id)}
          >
            ✓ Використано
          </button>
          <button
            className="nz-btn tiny"
            style={{ color: "var(--ink-3)" }}
            onClick={() => cancel(d.id)}
            aria-label="Скасувати знижку"
            title="Скасувати"
          >
            ✕
          </button>
        </div>
      ))}

      {showForm ? (
        <div style={{ display: "flex", gap: 6 }}>
          <input
            autoFocus
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="−20% за відгук / безкоштовний дизайн"
            style={{
              flex: 1,
              padding: "10px 12px",
              border: "1px solid var(--line)",
              borderRadius: 12,
              background: "var(--surface)",
              color: "var(--ink)",
              fontSize: 14,
              fontFamily: "inherit",
            }}
          />
          <button
            className="nz-btn primary"
            disabled={busy || !draft.trim()}
            onClick={create}
            style={{ height: 42, padding: "0 14px" }}
          >
            Додати
          </button>
          <button
            className="nz-btn"
            onClick={() => {
              setShowForm(false);
              setDraft("");
            }}
            style={{ height: 42, padding: "0 12px" }}
            aria-label="Скасувати додавання знижки"
            title="Скасувати"
          >
            ✕
          </button>
        </div>
      ) : (
        <button className="nz-create" onClick={() => setShowForm(true)}>
          ➕ Додати знижку
        </button>
      )}

      {history.length > 0 && (
        <details style={{ marginTop: 10 }}>
          <summary
            style={{
              cursor: "pointer",
              fontSize: 12,
              color: "var(--ink-3)",
              fontWeight: 700,
              padding: "4px 0",
            }}
          >
            Історія ({history.length})
          </summary>
          {history.map((d) => (
            <div
              key={d.id}
              style={{
                display: "flex",
                gap: 8,
                padding: "8px 10px",
                fontSize: 13,
                color: "var(--ink-3)",
                borderTop: "1px dashed var(--line)",
              }}
            >
              <span style={{ flex: 1, minWidth: 0 }}>{d.text}</span>
              <span>
                {d.status === "applied" ? "✓ використано" : "✕ скасовано"}
              </span>
            </div>
          ))}
        </details>
      )}
    </div>
  );
}
